// client/src/components/pages/LexiStar/Maintenance/Maintenance.tsx (ST-00249)
//
// Maintenance: route /lexistar/maintenance, sidebar LexiStar · Operate → Maintenance (next to the
// Status Board). Page permission lexistar.maintenance.view or .manage (config/pagePermissions);
// booking, reporting, logging, start / extend / close / cancel need .manage (in-page).
//
//   Now        how many trucks are broken down, in the workshop and delayed right now, and the
//              bookings of the next 7 days (GET /maintenance/events?state=active)
//   Timeline   one line per truck over the window (default: 2 days back, 7 ahead), drawn from the
//              time model (GET /time-model?grain=period&segments=true) — Breakdown, Planned
//              maintenance and Delay in their colours, Available as the empty track, bookings
//              striped, now marked; a stretch opens its event; each truck's available share
//   List       the window's events (GET /maintenance/events), filtered by truck, category and
//              state; a row opens the event (details, start / extend / close / cancel)
//   Actions    Report a breakdown (truck + what happened, starting now), Book maintenance, Log a
//              delay — EventDialog; ?book=breakdown|maintenance|delay&asset=<id> opens it
//              (deep link), ?event=<id> opens an event
// The window, filters and "every truck" live in the URL. Phone: the counts, the actions in the
// bottom bar, the timeline with the truck's name above its line, the list as cards.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaCarCrash, FaHourglassHalf, FaSyncAlt, FaWrench } from 'react-icons/fa';
import UnifiedDateRangePicker from '../../../common/UnifiedDateRangePicker';
import PhoneActionBar, { PhoneAction } from '../../../common/PhoneActionBar';
import { usePhoneLayout } from '../../../common/phoneLayout';
import { SegmentedControl, ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { toApiDateString } from '../../../../utils/dateUtils';
import { getTimeModel, listCodes, listEvents, errorText } from '../../../../services/lexistarMaintenance';
import type {
  AssetTimeModel,
  EventCategory,
  MaintenanceCode,
  TimeEvent,
} from '../../../../types/lexistar-maintenance';
import { EmptyState, ErrorBanner, LoadingState, NoPermissionPage, NoticeBanner, PageHeader, cardClasses } from '../StatusBoard/atoms';
import EventDialog, { type DialogAsset } from './EventDialog';
import EventDetailDialog from './EventDetailDialog';
import MaintenanceTimeline, { Legend } from './MaintenanceTimeline';
import {
  CATEGORY_META,
  EVENT_CATEGORY_ORDER,
  STATE_TEXT,
  addDays,
  eventDuration,
  eventWhen,
  fmtDuration,
  sastDay,
  summaryOf,
  windowOf,
  type BookingMode,
} from './maintenanceLogic';

export const VIEW = 'lexistar.maintenance.view';
export const MANAGE = 'lexistar.maintenance.manage';
/** The longest window the timeline draws. */
export const MAX_WINDOW_DAYS = 62;

type StateFilter = 'active' | 'open' | 'booked' | 'closed' | 'cancelled' | 'all';
const STATE_OPTIONS: Array<{ value: StateFilter; label: string }> = [
  { value: 'all', label: 'All but cancelled' },
  { value: 'active', label: 'Open or booked' },
  { value: 'open', label: 'Open now' },
  { value: 'booked', label: 'Booked' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];
type CategoryFilter = 'all' | EventCategory;
const BOOK_MODES: BookingMode[] = ['breakdown', 'maintenance', 'delay'];

const isDay = (s: string | null): s is string => !!s && /^\d{4}-\d{2}-\d{2}$/.test(s);
const parseDay = (s: string): Date => new Date(`${s}T12:00:00`);

function SummaryTile({ label, value, tone, hint }: { label: string; value: number | null; tone: 'breakdown' | 'planned_maintenance' | 'delay' | 'plain'; hint?: string }) {
  const meta = tone === 'plain' ? null : CATEGORY_META[tone];
  const active = value !== null && value > 0;
  return (
    <div
      role="group"
      aria-label={`${label}: ${value ?? 'not loaded yet'}`}
      className={`rounded-xl border px-4 py-3 ${active && meta ? meta.panel : 'bg-surface border-slate-200'}`}
    >
      <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600">
        {meta && <meta.Icon className={meta.text} aria-hidden="true" />}
        {label}
      </span>
      <span className={`block text-3xl font-bold tabular-nums leading-tight ${active && meta ? meta.text : 'text-slate-900'}`}>{value ?? '—'}</span>
      {hint && <span className="block text-xs text-slate-500">{hint}</span>}
    </div>
  );
}

function CategoryPill({ category }: { category: EventCategory }) {
  const m = CATEGORY_META[category];
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${m.pill}`}>
      <m.Icon aria-hidden="true" size={10} />
      {m.label}
    </span>
  );
}

function StatePill({ e }: { e: TimeEvent }) {
  const cls =
    e.state === 'open'
      ? e.overdue
        ? 'bg-amber-50 text-amber-800 ring-amber-200'
        : 'bg-sky-50 text-sky-800 ring-sky-200'
      : e.state === 'booked'
        ? 'bg-slate-100 text-slate-700 ring-slate-200'
        : e.state === 'cancelled'
          ? 'bg-surface text-slate-500 ring-slate-200 line-through'
          : 'bg-green-50 text-green-800 ring-green-200';
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap ring-1 ring-inset ${cls}`}>
      {e.overdue ? 'Open, overdue' : STATE_TEXT[e.state]}
    </span>
  );
}

const Maintenance = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission(VIEW) || hasPermission(MANAGE);
  const canManage = hasPermission(MANAGE);
  const phone = usePhoneLayout();
  const [params, setParams] = useSearchParams();

  const today = sastDay(Date.now());
  const from = isDay(params.get('from')) ? (params.get('from') as string) : addDays(today, -2);
  const toRaw = isDay(params.get('to')) ? (params.get('to') as string) : addDays(today, 7);
  const to = toRaw < from ? from : toRaw;
  const truck = params.get('truck') || '';
  const category = (EVENT_CATEGORY_ORDER as string[]).includes(params.get('cat') || '') ? (params.get('cat') as EventCategory) : 'all';
  const stateParam = params.get('state') as StateFilter | null;
  const state: StateFilter = STATE_OPTIONS.some((o) => o.value === stateParam) ? (stateParam as StateFilter) : 'all';
  const everyTruck = params.get('all') === '1';
  const bookParam = params.get('book') as BookingMode | null;
  const booking: BookingMode | null = bookParam && BOOK_MODES.includes(bookParam) ? bookParam : null;
  const bookAsset = params.get('asset');
  const openEventId = params.get('event');

  const [codes, setCodes] = useState<MaintenanceCode[]>([]);
  const [models, setModels] = useState<AssetTimeModel[]>([]);
  const [events, setEvents] = useState<TimeEvent[]>([]);
  const [active, setActive] = useState<TimeEvent[] | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [detail, setDetail] = useState<TimeEvent | null>(null);

  const setParam = useCallback(
    (changes: Record<string, string>) => {
      const next = new URLSearchParams(params);
      for (const [k, v] of Object.entries(changes)) {
        if (v) next.set(k, v);
        else next.delete(k);
      }
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const { fromMs, toMs } = windowOf(from, to);
  const tooLong = toMs - fromMs > MAX_WINDOW_DAYS * 86400000;

  const load = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const fromIso = new Date(fromMs).toISOString();
      const toIso = new Date(toMs).toISOString();
      const [codeList, model, list, act] = await Promise.all([
        listCodes(),
        tooLong ? Promise.resolve(null) : getTimeModel({ from: fromIso, to: toIso, grain: 'period', segments: true }),
        listEvents({
          from: fromIso,
          to: toIso,
          ...(truck ? { asset_id: [truck] } : {}),
          ...(category !== 'all' ? { category: [category] } : {}),
          ...(state === 'all' ? { state: ['booked', 'open', 'closed'] } : { state: [state] }),
          limit: 500,
        }),
        listEvents({ state: ['active'], limit: 1000 }),
      ]);
      setCodes(codeList.codes);
      setModels(model ? model.assets : []);
      setEvents(list.events);
      setActive(act.events);
      setNow(Date.parse(list.now) || Date.now());
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Could not load maintenance.'));
    } finally {
      setLoading(false);
    }
  }, [canView, fromMs, toMs, tooLong, truck, category, state]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // ?event=<id>: open it once the lists know it.
  useEffect(() => {
    if (!openEventId) return;
    const hit = events.find((e) => e.id === openEventId) || active?.find((e) => e.id === openEventId);
    if (hit) setDetail(hit);
  }, [openEventId, events, active]);

  const eventsById = useMemo(() => {
    const m = new Map<string, TimeEvent>();
    for (const e of [...(active || []), ...events]) m.set(e.id, e);
    return m;
  }, [events, active]);

  const assets: DialogAsset[] = useMemo(
    () => models.map((m) => ({ id: m.asset_id, registration: m.registration, fleet_no: m.fleet_no, tenant_id: m.tenant_id })),
    [models]
  );
  const shownModels = useMemo(() => {
    let list = models;
    if (truck) list = list.filter((m) => m.asset_id === truck);
    else if (!everyTruck) list = list.filter((m) => (m.segments || []).some((s) => s.category !== 'available'));
    return list;
  }, [models, truck, everyTruck]);
  const summary = active ? summaryOf(active, now) : null;

  const openBooking = (mode: BookingMode, assetId?: string | null) => setParam({ book: mode, asset: assetId || '' });
  const closeBooking = () => setParam({ book: '', asset: '' });
  const openDetail = (e: TimeEvent) => {
    setDetail(e);
    setParam({ event: e.id });
  };
  const closeDetail = () => {
    setDetail(null);
    setParam({ event: '' });
  };
  const changed = (_e: TimeEvent, message: string) => {
    setNotice(message);
    load();
  };

  const header = (
    <PageHeader
      icon={FaWrench}
      title="Maintenance"
      subtitle="Workshop bookings, breakdowns and delays per truck, and how much of its time each truck was available."
      actions={
        canView ? (
          <div className="flex flex-wrap items-center gap-2">
            {canManage && !phone && (
              <>
                <button
                  type="button"
                  onClick={() => openBooking('breakdown')}
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 rounded-lg shadow-sm"
                >
                  <FaCarCrash aria-hidden="true" /> Report a breakdown
                </button>
                <button
                  type="button"
                  onClick={() => openBooking('maintenance')}
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-semibold text-white bg-primary-500 hover:bg-primary-600 rounded-lg shadow-sm"
                >
                  <FaWrench aria-hidden="true" /> Book maintenance
                </button>
                <button
                  type="button"
                  onClick={() => openBooking('delay')}
                  className="inline-flex items-center gap-2 px-3.5 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-300 hover:bg-slate-50 rounded-lg"
                >
                  <FaHourglassHalf aria-hidden="true" /> Log a delay
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => load()}
              aria-label="Refresh"
              className={`${phone ? 'touch-target justify-center ' : ''}inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-surface border border-slate-200 rounded-lg hover:bg-slate-50`}
            >
              <FaSyncAlt size={12} className={loading ? 'animate-spin' : ''} aria-hidden="true" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return <NoPermissionPage header={header} message="You do not have permission to view maintenance." />;
  }

  const filters = (
    <div className={`flex flex-wrap items-end gap-3 ${phone ? 'phone-targets' : ''}`} role="group" aria-label="Window and filters">
      <div className="min-w-[14rem]">
        <span className="block text-xs font-medium text-slate-600 mb-1">Window</span>
        <UnifiedDateRangePicker
          startDate={parseDay(from)}
          endDate={parseDay(to)}
          onStartDateChange={(d) => setParam({ from: d ? toApiDateString(d) : '' })}
          onEndDateChange={(d) => setParam({ to: d ? toApiDateString(d) : '' })}
          bounds="all"
        />
      </div>
      <div className="min-w-[11rem] flex-1 sm:flex-none">
        <label htmlFor="maintenance-filter-truck" className="block text-xs font-medium text-slate-600 mb-1">
          Truck
        </label>
        <ThemedSelect id="maintenance-filter-truck" value={truck} onChange={(e) => setParam({ truck: String(e.target.value) })} aria-label="Truck">
          <option value="">All trucks</option>
          {assets.map((a) => (
            <option key={a.id} value={a.id}>
              {a.registration || a.id.slice(0, 8)}
            </option>
          ))}
        </ThemedSelect>
      </div>
      <div className="min-w-[10rem] flex-1 sm:flex-none">
        <label htmlFor="maintenance-filter-state" className="block text-xs font-medium text-slate-600 mb-1">
          State
        </label>
        <ThemedSelect id="maintenance-filter-state" value={state} onChange={(e) => setParam({ state: String(e.target.value) === 'all' ? '' : String(e.target.value) })} aria-label="State">
          {STATE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </ThemedSelect>
      </div>
      <SegmentedControl<CategoryFilter>
        options={[
          { value: 'all', label: 'All' },
          { value: 'breakdown', label: 'Breakdown' },
          { value: 'planned_maintenance', label: 'Planned', shortLabel: 'Planned' },
          { value: 'delay', label: 'Delay' },
        ]}
        value={category}
        onChange={(v) => setParam({ cat: v === 'all' ? '' : v })}
        ariaLabel="Category"
      />
    </div>
  );

  const timelineCard = (
    <section className={`${cardClasses} p-4 sm:p-5 space-y-3`} aria-labelledby="maintenance-timeline-title">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 id="maintenance-timeline-title" className="text-sm font-semibold text-slate-800">
          Timeline
        </h2>
        <Legend compact={phone} />
      </div>
      {tooLong ? (
        <p className="text-sm text-slate-600">Choose a window of {MAX_WINDOW_DAYS} days or less to draw the timeline.</p>
      ) : loading && models.length === 0 ? (
        <LoadingState label="Loading the timeline…" />
      ) : shownModels.length === 0 ? (
        <p className="text-sm text-slate-600">
          {models.length === 0 ? 'Your organisation has no trucks in the register yet.' : 'No maintenance, breakdown or delay in this window: every truck was available.'}
        </p>
      ) : (
        <MaintenanceTimeline assets={shownModels} fromMs={fromMs} toMs={toMs} now={now} eventsById={eventsById} onOpenEvent={(id) => eventsById.get(id) && openDetail(eventsById.get(id) as TimeEvent)} phone={phone} />
      )}
      {!truck && models.length > 0 && (
        <label className="inline-flex items-center gap-2 text-sm text-slate-700">
          <input type="checkbox" checked={everyTruck} onChange={(e) => setParam({ all: e.target.checked ? '1' : '' })} className="h-4 w-4 rounded border-slate-300" />
          Show every truck ({models.length}), not only those with events
        </label>
      )}
    </section>
  );

  const listCard = (
    <section className={`${cardClasses} ${phone ? 'p-3' : 'p-0 overflow-hidden'}`} aria-labelledby="maintenance-list-title">
      <div className={`flex items-center justify-between gap-3 ${phone ? 'px-1 pb-2' : 'px-5 pt-4 pb-3'}`}>
        <h2 id="maintenance-list-title" className="text-sm font-semibold text-slate-800">
          Events in the window
        </h2>
        <span className="text-xs text-slate-500">{events.length}</span>
      </div>
      {events.length === 0 ? (
        <div className="px-5 pb-5">
          <EmptyState icon={FaWrench} title={loading ? 'Loading…' : 'Nothing in this window'}>
            {loading ? null : canManage ? 'Book maintenance, report a breakdown or log a delay with the buttons above.' : 'No maintenance, breakdown or delay matches the filters.'}
          </EmptyState>
        </div>
      ) : phone ? (
        <ul className="space-y-2" aria-label="Events">
          {events.map((e) => (
            <li key={e.id}>
              <button type="button" onClick={() => openDetail(e)} className="touch-target w-full text-left rounded-xl border border-slate-200 bg-surface p-3 space-y-1">
                <span className="flex items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-slate-900">{e.registration || '—'}</span>
                  <StatePill e={e} />
                </span>
                <span className="flex items-center gap-2 min-w-0">
                  <CategoryPill category={e.category} />
                  <span className="truncate text-sm font-medium text-slate-800">{e.label}</span>
                </span>
                <span className="block text-xs text-slate-600">
                  {eventWhen(e, now)} · {fmtDuration(eventDuration(e, now))}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold text-slate-600">
              <tr>
                <th scope="col" className="px-5 py-2">Truck</th>
                <th scope="col" className="px-3 py-2">What</th>
                <th scope="col" className="px-3 py-2">When</th>
                <th scope="col" className="px-3 py-2 text-right">Time</th>
                <th scope="col" className="px-3 py-2">State</th>
                <th scope="col" className="px-3 py-2 whitespace-nowrap">Booked by</th>
                <th scope="col" className="px-5 py-2"><span className="sr-only">Open</span></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {events.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-5 py-2.5 font-mono font-semibold text-slate-900 whitespace-nowrap">{e.registration || '—'}</td>
                  <td className="px-3 py-2.5">
                    <span className="flex flex-wrap items-center gap-2">
                      <CategoryPill category={e.category} />
                      <span className="font-medium text-slate-800">{e.label}</span>
                    </span>
                    {e.notes && <span className="block text-xs text-slate-500 truncate max-w-[18rem]">{e.notes}</span>}
                  </td>
                  <td className="px-3 py-2.5 text-slate-700 min-w-[10rem]">{eventWhen(e, now)}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtDuration(eventDuration(e, now))}</td>
                  <td className="px-3 py-2.5">
                    <StatePill e={e} />
                  </td>
                  <td className="px-3 py-2.5 text-slate-600 truncate max-w-[12rem]">{e.booked_by}</td>
                  <td className="px-5 py-2.5 text-right">
                    <button type="button" onClick={() => openDetail(e)} className="text-sm font-medium text-primary-700 hover:underline" aria-label={`Open ${e.label} on ${e.registration || 'the truck'}`}>
                      {canManage && (e.state === 'open' || e.state === 'booked') ? 'Manage' : 'Open'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );

  const tiles = (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" aria-label="Right now">
      <SummaryTile label="Broken down now" value={summary ? summary.breakdownNow : null} tone="breakdown" />
      <SummaryTile label="In the workshop now" value={summary ? summary.workshopNow : null} tone="planned_maintenance" />
      <SummaryTile label="Delayed now" value={summary ? summary.delayedNow : null} tone="delay" />
      <SummaryTile
        label="Booked, next 7 days"
        value={summary ? summary.bookedAhead : null}
        tone="plain"
        hint={summary && summary.overdue ? `${summary.overdue} open past the expected end` : undefined}
      />
    </div>
  );

  const dialogs = (
    <>
      {booking && canManage && (
        <EventDialog
          mode={booking}
          assets={assets}
          codes={codes}
          assetId={bookAsset}
          onClose={closeBooking}
          onDone={changed}
          onOpenEvent={(e) => {
            // One URL change: the booking closes and the clashing event opens.
            setDetail(e);
            setParam({ book: '', asset: '', event: e.id });
          }}
        />
      )}
      {detail && <EventDetailDialog event={detail} canManage={canManage} onClose={closeDetail} onChanged={changed} />}
    </>
  );

  if (phone) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50" data-phone-layout="">
        {header}
        <div className="flex-1 pt-4 pb-6 space-y-3">
          <ErrorBanner message={error} onDismiss={() => setError(null)} />
          <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
          {tiles}
          {filters}
          {timelineCard}
          {listCard}
        </div>
        {canManage && (
          <PhoneActionBar label="Put a truck's time on record">
            <PhoneAction variant="danger" icon={FaCarCrash} onClick={() => openBooking('breakdown')}>
              Breakdown
            </PhoneAction>
            <PhoneAction variant="primary" icon={FaWrench} onClick={() => openBooking('maintenance')}>
              Maintenance
            </PhoneAction>
            <PhoneAction icon={FaHourglassHalf} onClick={() => openBooking('delay')}>
              Delay
            </PhoneAction>
          </PhoneActionBar>
        )}
        <div className="phone-targets">{dialogs}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1600px] mx-auto px-3 sm:px-6 py-6 space-y-5">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
        <NoticeBanner message={notice} onDismiss={() => setNotice(null)} />
        {tiles}
        {filters}
        {timelineCard}
        {listCard}
      </div>
      {dialogs}
    </div>
  );
};

export default Maintenance;
